import React, {
  useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useQuery } from '../../../../utils/hooks/useQuery';
import utils from '../../../../utils/utils';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import { clearProduct, getTopGroups, searchProduct } from '../../../../store/actions/dashboard';
import classes from '../TopMedications/medication.module.scss';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import Select from '../../../_common/Form/Selects/Select/Select';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import { specOptions } from '../../../../utils/data/data';
import InputField from '../../../_common/Form/TextFields/InputField';
import Button from '../../../_common/Form/Buttons/Button';
import Loader from '../../../_common/Loader/Loader';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import accountHelpers from '../../../../utils/accountHelpers';

const tableHeader = [
  {
    name: 'Name',
    path: 'name',
    isSelectable: true,
  },
  {
    name: 'Group',
    path: 'group',
  },
  {
    name: 'Patients',
    path: 'patients',
    isSelectable: true,
  },
  {
    name: 'Prescription Count',
    path: 'sales',
    isSelectable: true,
  },
  {
    name: 'Revenue per sale',
    path: 'revenuePerSale',
    isSelectable: true,
  },
  {
    name: 'Total Spent',
    path: 'revenue',
    isSelectable: true,
  },
  {
    name: 'Cost per sale',
    path: 'costPerSale',
    isSelectable: true,
  },
  {
    name: 'Cost',
    path: 'cost',
    isSelectable: true,
  },
  {
    name: 'Profit',
    path: 'profit',
    isSelectable: true,
  },
  {
    name: 'Margin',
    path: 'margin',
    isSelectable: true,
  },
];

const typeData = [
  {
    title: 'RX',
    key: 'rxKey',
    value: 'rx',
  },
  {
    title: 'OTC',
    key: 'otcKey',
    value: 'otc',
  },
];

const specialData = [
  {
    title: 'SortPak',
    key: 'salesCode',
    value: 'M',
  },
];

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).add(1, 'day').format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).add(1, 'day').format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');
let firstLoad = true;
const limitRep = 20;
let timeout;

const defaultSorting = {
  sortBy: 'name',
  sort: 'asc',
};

const TopGroups = () => {
  const { query, setQuery, queryToStringify } = useQuery();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { dashboardTotalPage, dashboardPageData, dashboardProduct } = useSelector((store) => store.dashboard);
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    specialization: query.specialization || '',
    productId: query.productId || '',
    zip: query.zip || '',
    rep: query.rep || '',
    page: query.page || 1,
    limit: query.limit || 15,
  });
  const [searchProductValue, setSearchProductValue] = useState(query.productName || '');
  const [searchProductLoading, setSearchProductLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [sorting, setSorting] = useState({
    sortBy: query.sortBy || 'name',
    sort: query.sort || 'asc',
  });

  const { sort, sortBy } = sorting;

  const {
    page, limit, specialization, zip, rep, start, end, productId,
  } = requestData;

  useLayoutEffect(() => () => firstLoad = true, []);

  useEffect(() => {
    if (firstLoad) {
      dispatch(getSelectRepListRequest({ limit: limitRep, role: 'Rep' }));
    }

    (async () => {
      setLoading(true);
      await dispatch(getTopGroups(collectRequestData(requestData)));
      setLoading(false);
      firstLoad = false;
    })();
  }, []);

  useEffect(() => {
    setQuery({ ...query, ...sorting });
  }, [sorting]);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const changeProduct = (value) => {
    setSearchProductValue(value);

    clearTimeout(timeout);
    dispatch(clearProduct());

    if (value.trim()) {
      setSearchProductLoading(true);

      timeout = setTimeout(async () => {
        await dispatch(searchProduct(value));
        setSearchProductLoading(false);
      }, 400);
    } else {
      setSearchProductLoading(false);
    }
  };

  const nextOptionListRequest = async (repPage) => {
    await dispatch(getSelectRepListRequest({ page: repPage, limit: limitRep, role: 'Rep' }));
  };

  const changeRep = (valueRep) => {
    setRequestData((prev) => ({ ...prev, rep: valueRep }));
  };

  const changeZip = (value) => {
    if (!Number.isNaN(+value) || value === '') {
      setRequestData((prev) => ({ ...prev, zip: value }));
    }
  };

  const changeRequestData = (path, value, searchValue) => {
    if (searchValue) setSearchProductValue(searchValue);

    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const clearFilters = async () => {
    const defaultRequestData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      page: 1,
      limit: 15,
    };

    setSearchProductValue('');

    setRequestData(defaultRequestData);

    setSorting(defaultSorting);

    setQuery({ ...defaultRequestData, ...defaultSorting });

    setLoading(true);
    await dispatch(getTopGroups(defaultRequestData));
    setLoading(false);
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') {
      newRequestData.page = 1;
    }

    const data = collectRequestData(newRequestData);
    await dispatch(getTopGroups(data));

    setRequestData(newRequestData);

    if (data.productId) data.productName = searchProductValue;

    setQuery({ ...data, ...sorting });
  };

  const submitFilters = async (e) => {
    e.preventDefault();

    const data = collectRequestData(requestData);
    data.page = 1;

    setLoading(true);
    await dispatch(getTopGroups(data));
    setRequestData((prev) => ({ ...prev, page: 1 }));

    if (data.productId) data.productName = searchProductValue;

    setQuery({ ...data, ...sorting });
    setLoading(false);
  };

  const singleRepPageClick = (name) => {
    if (accountHelpers.getUser().role === 'Admin') {
      const queryRequest = queryToStringify({
        specialization,
        zip,
        rep,
        start,
        end,
        productId,
        name,
        report: 'Group',
      });

      navigate(`/dashboard/report-single${queryRequest}`);
    }
  };

  const sortedData = useMemo(() => _.orderBy(dashboardPageData, (d) => (!d[sortBy] || isNaN(d[sortBy]) ? d[sortBy] : +d[sortBy]), sort), [dashboardPageData, sorting]);

  return (
    <div className={classes.wrapper}>
      <form onSubmit={submitFilters} className={classes.wrapper_header}>
        <div className={classes.first_box_container}>
          <div style={{ marginRight: 10 }}>
            <RangeDatePicker
              currentDate={requestData}
              onChangeDate={onChangeDate}
            />
          </div>

          <div className={classes.filter_button}>
            <FilterButton activeValue="rx" buttonsData={typeData} onClick={() => {}} />
          </div>

          <div className={classes.filter_button_sort_pak}>
            <FilterButton activeValue="M" buttonsData={specialData} onClick={() => {}} />
          </div>
        </div>

        <div className={classes.second_box_container}>

          <div className={classes.select_rep}>
            <Select
              label="All reps"
              nextOptionListRequest={nextOptionListRequest}
              options={selectRepList}
              totalPage={pageCount}
              currentPage={currentPage}
              limit={limitRep}
              currentOption={rep}
              onChange={changeRep}
              optionPathname="name"
            />
          </div>

          <div className={classes.product}>
            <SelectWithSearch
              label="Product"
              optionPathname="name"
              optionKey="id"
              options={dashboardProduct}
              searchValue={searchProductValue}
              onChange={changeProduct}
              loading={searchProductLoading}
              onSelectOption={(option, searchValue) => changeRequestData('productId', option, searchValue)}
            />
          </div>

          <div className={classes.specialization}>
            <Select
              label="All specializations"
              options={specOptions}
              currentOption={specialization}
              onChange={(option) => changeRequestData('specialization', option)}
            />
          </div>

          <div className={classes.product}>
            <InputField
              label="Zip code"
              value={zip}
              onChange={changeZip}
            />
          </div>

          <div className={classes.buttonContainer}>
            <Button
              className={classes.updateButton}
              title="Update"
              type="submit"
            />

            <Button
              className={classes.updateButton}
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>
      </form>
      <div className={classes.table_wrapper}>
        {loading
          ? <Loader />

          : (
            <TableAndPagination
              onClick={singleRepPageClick}
              onChange={onChangePageData}
              data={sortedData}
              sort={sort}
              sortBy={sortBy}
              onSortBy={setSorting}
              header={tableHeader}
              totalPage={+dashboardTotalPage}
              page={+page - 1}
              limit={+limit}
            />
          )}
      </div>
    </div>
  );
};

export default TopGroups;
